import { Button, Text } from '@mantine/core'
import { useLocalStorage, useMediaQuery, useViewportSize } from '@mantine/hooks'
import { IconEye } from '@tabler/icons-react'
import { formatTime } from '@tnn/core'
import { type PointerEvent, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../api/client'
import { appLink } from '../app/links'
import classes from './ViewAs.module.css'

/** Where the badge rests: against the left or right edge, this far from the top (px) */
interface Spot {
  side: 'left' | 'right'
  y: number
}

/** From the screen's edges; the top bar above and, on a phone, the bottom bar below are kept clear */
const GAP = 16

/**
 * Over every app while a super admin looks at them as someone (apps/api/src/auth/view-as.ts): a badge saying as whom,
 * read only, until when, and back to themselves at that person's page in Admin, where they started. Dragged anywhere,
 * it settles against the nearer side where it was let go, and stays there on this browser. Its time up, the page loads
 * again as the super admin
 */
export function ViewAsBadge({ userId, name, until }: { userId: string; name: string; until: string }) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const phone = useMediaQuery('(max-width: 47.99em)', false, { getInitialValueInEffect: false })
  const { height: screen } = useViewportSize()
  const [spot, setSpot] = useLocalStorage<Spot | null>({ key: 'tnn.view-as', defaultValue: null, getInitialValueInEffect: false })
  const badge = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(56)
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null)
  const from = useRef<{ pointerX: number; pointerY: number; x: number; y: number } | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => window.location.reload(), Math.max(0, new Date(until).getTime() - Date.now()) + 1000)
    return () => clearTimeout(timer)
  }, [until])
  useLayoutEffect(() => setHeight(badge.current?.offsetHeight ?? 56), [])

  const top = (phone ? 72 : 64) + GAP / 2
  const lowest = screen - height - (phone ? 80 + GAP : GAP)
  const within = (y: number) => Math.max(top, Math.min(y, lowest))
  // At first: on the right, above a phone's floating button and a list's paging
  const rest = { side: spot?.side ?? 'right', y: within(spot?.y ?? screen - height - (phone ? 176 : 96)) }
  // The screen's width without its scroll bar: the right edge the badge rests against
  const right = `calc(${document.documentElement.clientWidth - GAP}px - 100%)`
  const transform = drag ? `translate(${drag.x}px, ${drag.y}px)` : `translate(${rest.side === 'left' ? `${GAP}px` : right}, ${rest.y}px)`

  const grab = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest('button')) return
    const box = event.currentTarget.getBoundingClientRect()
    from.current = { pointerX: event.clientX, pointerY: event.clientY, x: box.left, y: box.top }
    event.currentTarget.setPointerCapture(event.pointerId)
    setDrag({ x: box.left, y: box.top })
  }
  const move = (event: PointerEvent<HTMLDivElement>) => {
    const start = from.current
    if (!start) return
    setDrag({ x: start.x + event.clientX - start.pointerX, y: start.y + event.clientY - start.pointerY })
  }
  const drop = (event: PointerEvent<HTMLDivElement>) => {
    if (!from.current || !drag) return
    from.current = null
    const middle = drag.x + event.currentTarget.offsetWidth / 2
    setSpot({ side: middle < document.documentElement.clientWidth / 2 ? 'left' : 'right', y: within(drag.y) })
    setDrag(null)
  }

  const stop = async () => {
    setBusy(true)
    await api.delete('/me/view-as').catch(() => undefined)
    window.location.assign(appLink('admin', `/users/${userId}`))
  }

  return (
    <div
      ref={badge}
      className={classes.badge}
      style={{ transform }}
      data-dragging={drag ? true : undefined}
      role="status"
      title={t('viewAs.move')}
      onPointerDown={grab}
      onPointerMove={move}
      onPointerUp={drop}
      onPointerCancel={drop}
    >
      <IconEye size={22} stroke={1.75} className={classes.icon} />
      <div className={classes.text}>
        <Text size="sm" fw={500} truncate>
          {t('viewAs.title', { name })}
        </Text>
        <Text size="xs" truncate>
          {t('viewAs.until', { time: formatTime(until) })}
        </Text>
      </div>
      <Button size="compact-sm" radius="xl" color="warning" className={classes.stop} onClick={stop} loading={busy}>
        {t('viewAs.stop')}
      </Button>
    </div>
  )
}
